import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";

import {
  useRejectCandidate,
  rejectionReasons,
} from "@/mutations/company/opening/reject";
import { MultiSelect } from "@/components/ui/multi-select";

const rejectCandidateOpportunitySchema = z.object({
  openingId: z.string(),
  candidateStatusId: z.string(),
  declineReasons: z
    .array(
      z
        .string()
        .refine(
          (string) => rejectionReasons.includes(string),
          "The reason(s) provided are invalid.",
        ),
    )
    .nonempty(),
  otherDeclineReason: z.string().optional(),
});

export const RejectCandidateOpportunityDialog = ({
  openingId,
  candidateStatusId,
  button,
}: {
  openingId: string;
  candidateStatusId: string;
  button?: React.ReactNode;
}) => {
  const form = useForm<z.infer<typeof rejectCandidateOpportunitySchema>>({
    resolver: zodResolver(rejectCandidateOpportunitySchema),
    mode: "onChange",
    defaultValues: {
      openingId,
      candidateStatusId,
      declineReasons: [],
      otherDeclineReason: "",
    },
  });

  const { rejectCandidate, isRejecting } = useRejectCandidate();

  const [open, setOpen] = useState<boolean>(false);

  function onSubmit() {
    if (isRejecting) return;
    void form.handleSubmit((data) => rejectCandidate(data))();
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {button ? button : <Button>Reject</Button>}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Are You Sure You Want to Reject This Candidate?
          </DialogTitle>
          <DialogDescription>
            Please let us know why you are rejecting this candidate so that we
            can better understand what you&apos;re looking for. The candidate
            will be informed of your decision. Your feedback is for internal use
            only and will not be shared with the candidate.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form className="w-full flex flex-col gap-4">
            <FormField
              control={form.control}
              name="declineReasons"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reasons</FormLabel>
                  <FormControl>
                    <MultiSelect
                      values={field.value}
                      setValues={field.onChange}
                      options={rejectionReasons}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="otherDeclineReason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Other Reason(s)</FormLabel>
                  <FormControl>
                    <Textarea {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            disabled={isRejecting || !form.formState.isValid}
            variant="secondary"
            onClick={onSubmit}
          >
            {isRejecting && <Loader2 className="animate-spin" />}
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
